// src/components/ShowPage.js
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import socket from '../socket';
import './css/ShowPage.css';
import TeamSheet from './show/TeamSheet';
import TeamJoinPanel from './show/TeamJoinPanel';
import ViewerJoinInfo from './show/ViewerJoinInfo';
import { fetchJson, resolveMediaUrl } from '../utils/api';
import { getTeamIdFromPlayer } from '../utils/player';
import { isValidHttpUrl } from '../utils/url';
import ProgressBar from './ui/ProgressBar';
import Button from './ui/Button';
import { normalizeAttachments } from '../utils/attachments';
import FinaleQuizpokerStage from './FinaleQuizpokerStage';
import FinaleFinalfrageStage from './FinaleFinalfrageStage';
import { fromServerQuestion } from '../integrations/quizApiAdapter';
import useAttachmentControls from '../hooks/useAttachmentControls';
import MatrixGrid from './MatrixGrid';
import DecodeText from './ui/DecodeText';
import { getFinaleRound, normalizeFinaleState } from '../utils/finale';
import useSocketEvent from '../utils/useSocketEvent';
import { formatDeltaMs } from '../utils/formatDeltaMs';
import safeStorage from '../utils/safeStorage';

const formatPlaybackTime = (value = 0) => {
    if (!Number.isFinite(value)) return '0:00';
    const minutes = Math.floor(value / 60);
    const seconds = Math.max(0, Math.floor(value % 60));
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const LOOP_FADE_DURATION = 1.0; // Default-Fallback für setupLoopFade

const DEFAULT_MUSIC_CONFIG = {
    inter:             { url: '/sounds/inter_loop.mp3',    volume: 0.05, fadeDuration: 1.0, loop: true },
    question:          { url: '/sounds/question_loop.mp3', volume: 0.40, fadeDuration: 1.0, loop: true },
    reveal:            { url: '/sounds/reveal_loop.mp3',   volume: 0.40, fadeDuration: 1.0, loop: true },
    finale_decoder:    { url: '/sounds/finale_loop.mp3',   volume: 0.15, fadeDuration: 1.0, loop: true },
    finale_quizpoker:  { url: '/sounds/finale_loop.mp3',   volume: 0.15, fadeDuration: 1.0, loop: true },
    finale_finalfrage: { url: '/sounds/finale_loop.mp3',   volume: 0.15, fadeDuration: 1.0, loop: true },
};
const TEAM_CHAT_SAVE_DELAY_MS = 220;

// Vergleicht zwei (kleine) Configs strukturell. Wird genutzt, um Audio-States
// NICHT bei jedem Broadcast neu zu setzen, wenn sich inhaltlich nichts geaendert
// hat – sonst startet die Hintergrundmusik staendig neu.
const sameJson = (a, b) => {
    try { return JSON.stringify(a) === JSON.stringify(b); } catch (_) { return false; }
};

const setupLoopFade = (audio, baseVolume, fadeDuration = LOOP_FADE_DURATION) => {
    if (!audio) return () => {};

    audio.loop = false;

    const handlePlay = () => {
        if (audio.currentTime === 0) {
            audio.volume = 0;
        }
    };

    const handleTimeUpdate = () => {
        if (!audio.duration || Number.isNaN(audio.duration)) return;
        const remaining = audio.duration - audio.currentTime;
        if (audio.currentTime < fadeDuration) {
            audio.volume = baseVolume * Math.min(audio.currentTime / fadeDuration, 1);
        } else if (remaining < fadeDuration) {
            audio.volume = baseVolume * Math.max(remaining / fadeDuration, 0);
        } else if (audio.volume !== baseVolume) {
            audio.volume = baseVolume;
        }
    };

    const handleEnded = () => {
        audio.currentTime = 0;
        audio.play().catch(() => {});
    };

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
        audio.removeEventListener('play', handlePlay);
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('ended', handleEnded);
    };
};

const MediaProgress = ({ progress }) => {
    const duration = progress?.duration || 0;
    const current = Math.max(0, progress?.current || 0);
    const percent = duration ? Math.min(current / duration, 1) * 100 : 0;

    return (
        <div className="media-progress">
            <div className="media-progress-track">
                <div className="media-progress-bar" style={{ width: `${percent}%` }} />
            </div>
            <div className="media-progress-label">
                {formatPlaybackTime(current)}
                {duration ? ` / ${formatPlaybackTime(duration)}` : ''}
            </div>
        </div>
    );
};

const MediaAttachment = ({ entry, variant = 'inline', onTimeSync }) => {
    const mediaRef = useRef(null);
    const attachmentKey = entry?.attachment?.urlKey || entry?.attachment?.id;
    const [progress, setProgress] = useState({
        current: entry?.ctrl?.currentTime ?? entry?.ctrl?.startAtSec ?? 0,
        duration: entry?.ctrl?.duration ?? 0,
    });
    const [playbackBlocked, setPlaybackBlocked] = useState(false);
    const [buffering, setBuffering] = useState(false);

    useEffect(() => {
        setProgress({
            current: entry?.ctrl?.currentTime ?? entry?.ctrl?.startAtSec ?? 0,
            duration: entry?.ctrl?.duration ?? 0,
        });
    }, [
        attachmentKey,
        entry?.ctrl?.currentTime,
        entry?.ctrl?.duration,
        entry?.ctrl?.startAtSec,
    ]);

    useEffect(() => {
        if (!entry || !entry.attachment || !entry.ctrl) return;
        if (entry.attachment.kind !== 'audio' && entry.attachment.kind !== 'video') return;
        const el = mediaRef.current;
        if (!el) return;

        const ctrl = entry.ctrl;
        const desiredTime = ctrl.state === 'stopped'
            ? ctrl.startAtSec ?? 0
            : ctrl.currentTime ?? ctrl.startAtSec ?? 0;

        const applyTime = () => {
            const drift = Math.abs((el.currentTime || 0) - desiredTime);
            if (Number.isFinite(desiredTime) && drift > 0.75) {
                el.currentTime = desiredTime;
            }
            setProgress((prev) => ({
                current: Number.isFinite(desiredTime) ? desiredTime : prev.current,
                duration: prev.duration || ctrl.duration || 0,
            }));
        };

        const pauseAndReset = () => {
            applyTime();
            el.pause();
            setBuffering(false);
            setPlaybackBlocked(false);
        };

        if (!ctrl.visible) {
            pauseAndReset();
            return;
        }

        if (ctrl.state === 'playing') {
            applyTime();
            if (el.readyState < 3) setBuffering(true);
            el.play()
                .then(() => {
                    setPlaybackBlocked(false);
                    setBuffering(false);
                })
                .catch(() => setPlaybackBlocked(true));
        } else if (ctrl.state === 'paused') {
            applyTime();
            el.pause();
            setPlaybackBlocked(false);
        } else {
            pauseAndReset();
        }
    }, [
        entry,
        entry?.ctrl?.visible,
        entry?.ctrl?.state,
        entry?.ctrl?.startAtSec,
        entry?.ctrl?.currentTime,
        entry?.attachment?.url,
        entry?.attachment?.kind,
    ]);

    useEffect(() => {
        if (!entry || !entry.attachment || !entry.ctrl) return;
        if (entry.attachment.kind !== 'audio' && entry.attachment.kind !== 'video') return;
        const el = mediaRef.current;
        if (!el) return;

        const updateProgress = () => {
            const duration = Number.isFinite(el.duration) ? el.duration : progress.duration;
            const current = Number.isFinite(el.currentTime) ? el.currentTime : 0;
            setProgress({ current, duration: duration || 0 });
            if (entry.ctrl.state === 'playing' && onTimeSync) {
                onTimeSync({
                    questionId: entry.ctrl.questionId,
                    attachmentId: entry.attachment.id,
                    url: entry.attachment.url,
                    urlKey: entry.attachment.urlKey,
                    currentTime: current,
                    duration: duration || 0,
                    updatedAt: Date.now(),
                });
            }
        };

        const handleWaiting = () => setBuffering(true);
        const handlePlaying = () => setBuffering(false);
        const handleCanPlay = () => setBuffering(false);

        el.addEventListener('timeupdate', updateProgress);
        el.addEventListener('loadedmetadata', updateProgress);
        el.addEventListener('durationchange', updateProgress);
        el.addEventListener('waiting', handleWaiting);
        el.addEventListener('playing', handlePlaying);
        el.addEventListener('canplay', handleCanPlay);

        return () => {
            el.removeEventListener('timeupdate', updateProgress);
            el.removeEventListener('loadedmetadata', updateProgress);
            el.removeEventListener('durationchange', updateProgress);
            el.removeEventListener('waiting', handleWaiting);
            el.removeEventListener('playing', handlePlaying);
            el.removeEventListener('canplay', handleCanPlay);
        };
    }, [entry, onTimeSync, progress.duration]);

    if (!entry || !entry.attachment || !entry.ctrl) return null;
    const { attachment } = entry;
    const resolvedUrl = resolveMediaUrl(attachment.url);
    const displayName = attachment.title || attachment.displayName || attachment.originalName || attachment.url;
    const blocked = playbackBlocked && (attachment.kind === 'audio' || attachment.kind === 'video');

    const retryPlayback = () => {
        const el = mediaRef.current;
        if (!el) return;
        el.play().then(() => setPlaybackBlocked(false)).catch(() => setPlaybackBlocked(true));
    };

    const classNames = ['media-content', `media-${variant}`, `kind-${attachment.kind}`];

    return (
        <div className={classNames.join(' ')} onClick={blocked ? retryPlayback : undefined}>
            {attachment.kind === 'image' && (
                <img
                    src={resolvedUrl}
                    alt={displayName || 'Attachment'}
                    className="media-image"
                />
            )}
            {attachment.kind === 'video' && (
                <div className="media-video-wrapper">
                    <video
                        ref={mediaRef}
                        src={resolvedUrl}
                        playsInline
                        preload="auto"
                        controls={false}
                        className="media-video"
                    />
                    {buffering && <div className="media-buffering">Lädt…</div>}
                    <MediaProgress progress={progress} />
                </div>
            )}
            {attachment.kind === 'audio' && (
                <div className="media-audio-wrapper">
                    <audio
                        ref={mediaRef}
                        src={resolvedUrl}
                        preload="auto"
                        controls={false}
                        className="media-hidden-element"
                    />
                    <div className="media-audio-title">🎵 {displayName || 'Audio'}</div>
                    <MediaProgress progress={progress} />
                </div>
            )}
            {attachment.kind === 'file' && (
                <a className="media-file-link" href={resolvedUrl} target="_blank" rel="noreferrer">
                    Datei öffnen
                </a>
            )}
            {blocked && <div className="media-playback-hint">Tap to enable audio/video</div>}
        </div>
    );
};

function ShowPage() {
    // =========== 1) STATES ===========
    const [myTeamPrediction, setMyTeamPrediction] = useState(null);
    const [quizActive, setQuizActive] = useState(false);
    const [paused, setPaused] = useState(false);
    const [currentQuestion, setCurrentQuestion] = useState(null);

    // Haupt-Countdown
    const [countdown, setCountdown] = useState(null);
    const [initialCountdown, setInitialCountdown] = useState(null);
    const [countdownDurationMs, setCountdownDurationMs] = useState(null);
    const [countdownEndsAtMs, setCountdownEndsAtMs] = useState(null);

    // Prediction Countdown
    const [predictionCountdown, setPredictionCountdown] = useState(null);
    const [initialPredictionCountdown, setInitialPredictionCountdown] = useState(null);
    const [predictionDurationMs, setPredictionDurationMs] = useState(null);
    const [predictionEndsAtMs, setPredictionEndsAtMs] = useState(null);

    const [, setShowPredictions] = useState(false);
    const [, setAllPredictions] = useState({});

    const [predictionActive, setPredictionActive] = useState(false);
    const [predictionAnswers, setPredictionAnswers] = useState({});
    const [predictionsRevealed, setPredictionsRevealed] = useState(false);
    const [isPredictionDrawerOpen, setIsPredictionDrawerOpen] = useState(false);

    // Teams & Spieler
    const [teams, setTeams] = useState([]);
    const [allPlayers, setAllPlayers] = useState([]);

    // Aktives Team
    const [activeTeamId, setActiveTeamId] = useState(null);

    // Antworten pro Team => { teamId: answerIndex }
    const [selectedAnswers, setSelectedAnswers] = useState({});

    // Dein Team
    const [yourTeamId, setYourTeamId] = useState(null);
    const [myTeamJoinCode, setMyTeamJoinCode] = useState(null);
    const moderatorTeam = teams.find(team => team.name.toLowerCase() === 'moderator');
    const regularTeams = teams.filter(team => team.id !== (moderatorTeam ? moderatorTeam.id : null));

    // Richtige Antwort
    const [correctAnswerIndex, setCorrectAnswerIndex] = useState(null);

    // Lokaler State: eigene Auswahl
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [besserwisserState, setBesserwisserState] = useState(null);
    const [besserwisserSubmitted, setBesserwisserSubmitted] = useState(false);
    const [besserwisserQuestionId, setBesserwisserQuestionId] = useState(null);
    const [jokerBarsQuestionId, setJokerBarsQuestionId] = useState(null);
    const previousJokerUsedRef = useRef(false);

    // Sichtbarkeit Frage + Antworten
    const [displayedQuestion, setDisplayedQuestion] = useState(false);
    const [displayedAnswers, setDisplayedAnswers] = useState([]);

    const [isModeratorPanelOpen, setIsModeratorPanelOpen] = useState(false);
    const [moderatorScale, setModeratorScale] = useState(1);
    const [teamSharedText, setTeamSharedText] = useState('');
    const [hasUnreadTeamSharedText, setHasUnreadTeamSharedText] = useState(false);

    const [soundStatus, setSoundStatus] = useState({
        questionPlaying: false,
        revealPlaying: false,
    });
    const [soundPlayedForQuestion, setSoundPlayedForQuestion] = useState(null);

    // Hintergrundmusik (moderator-gesteuert)
    const bgMusicRef = useRef(null);
    const [bgMusicState, setBgMusicState] = useState({ url: null, volume: 0.5, playing: false, fadeDuration: 1.0, loop: true });

    // ManualAudio (Testwiedergabe aus Optionen)
    const manualAudioRef = useRef(null);
    const [manualAudioState, setManualAudioState] = useState({ url: null, volume: 0.5, playing: false, loop: false });

    // Phasenmusik-Konfiguration (vom Server, persistent)
    const [musicConfig, setMusicConfig] = useState(DEFAULT_MUSIC_CONFIG);

    // Sound-Effekte-Konfiguration (vom Server, persistent)
    const [soundEffectsConfig, setSoundEffectsConfig] = useState({
        correct: { url: '/sounds/correct.mp3', volume: 0.5 },
        wrong:   { url: '/sounds/wrong.mp3',   volume: 0.5 },
        select:  { url: '/sounds/select.mp3',  volume: 0.5 },
    });

    const [showQuestionContainer, setShowQuestionContainer] = useState(false);
    const { getState: getAttachmentState, actions: attachmentActions } = useAttachmentControls();

    const soundsRef = useRef({
        inter: new Audio('/sounds/inter_loop.mp3'),
        question: new Audio('/sounds/question_loop.mp3'),
        correct: new Audio('/sounds/correct.mp3'),
        correct2: new Audio('/sounds/correct2.mp3'),
        wrong: new Audio('/sounds/wrong.mp3'),
        wrong2: new Audio('/sounds/wrong2.mp3'),
        reveal: new Audio('/sounds/reveal_loop.mp3'),
        select: new Audio('/sounds/select.mp3'),
    });
    const sounds = soundsRef.current;

    // === NEU für Matrix ===
    const [matrixVisible, setMatrixVisible] = useState(false);

    // !!! Nicht direkt auf null setzen, wenn "visible=false" => wir brauchen das fürs Ausblenden
    const [matrixData, setMatrixData] = useState(null);

    const [selectedMatrixCellId, setSelectedMatrixCellId] = useState(null);

    const [finale, setFinale] = useState(null);
    const [quizpokerFinale, setQuizpokerFinale] = useState(null);
    const [finalfrageFinale, setFinalfrageFinale] = useState(null);
    const [finaleRevealToken, setFinaleRevealToken] = useState(0);
    const finaleAudioRef = useRef(null);
    const [finaleAudioBlocked, setFinaleAudioBlocked] = useState(false);

    // Drei Stadien: in, idle, out
    const [showMatrix, setShowMatrix] = useState(false);
    const [matrixAnimationStage, setMatrixAnimationStage] = useState('idle');
    const [matrixDelays, setMatrixDelays] = useState({});

    const getSafeLink = (link) => (isValidHttpUrl(link) ? link.trim() : null);
    const moderatorPlayersWithLinks = moderatorTeam?.spieler?.filter(player => getSafeLink(player.link)) ?? [];
    const hasModeratorStream = moderatorPlayersWithLinks.length > 0;
    const currentTeam = teams.find((team) => String(team.id) === String(yourTeamId));
    const yourTeamSharedText = currentTeam?.sharedText ?? '';
    const playerId = useMemo(() => safeStorage.getItem('playerId'), []);
    const previousFinalePhase = useRef(null);
    const finaleActive = !!finale?.active || !!quizpokerFinale?.active || !!finalfrageFinale?.active;
    // Bei "Finalfrage" endet die Finale-Musik, sobald verrechnet wurde (Phase resolved) –
    // dann uebernimmt wieder die Zwischenphasen-Musik.
    const finalfrageResolved = !!finalfrageFinale?.active && finalfrageFinale?.live?.phase === 'resolved';
    const finaleMusicActive = finaleActive && !finalfrageResolved;
    // Stabiler String – verhindert Effekt-Re-Runs (und damit Musik-Neustarts) bei jedem Broadcast.
    const finaleConfigKey = useMemo(() => (
        finalfrageFinale !== null ? 'finale_finalfrage'
            : quizpokerFinale !== null ? 'finale_quizpoker'
            : 'finale_decoder'
    ), [finalfrageFinale, quizpokerFinale]);
    const previousTeamIdRef = useRef(yourTeamId);
    const hasMountedRef = useRef(false);
    const lastSeenTeamSharedTextRef = useRef(yourTeamSharedText);

    const tryPlayAudio = useCallback(async (audio, label = 'audio', options = {}) => {
        if (!audio) return false;

        try {
            await audio.play();
            return true;
        } catch (error) {
            console.warn(`[ShowPage] Konnte Audio nicht abspielen (${label}):`, error);
            if (typeof options.onBlocked === 'function') {
                options.onBlocked(error);
            }
            return false;
        }
    }, []);

    // --------------------------------------
    // useEffects etc.
    // --------------------------------------

    // Einmalig: Error-Listener für alle Sound-Objekte + Effekt-Lautstärken
    useEffect(() => {
        sounds.correct.volume = 0.5;
        sounds.correct2.volume = 0.5;
        sounds.wrong.volume = 0.5;
        sounds.wrong2.volume = 0.5;
        sounds.select.volume = 0.5;

        const handlers = {};
        Object.keys(sounds).forEach(key => {
            handlers[key] = (e) => console.error(`Fehler beim Laden des Sounds '${key}':`, e);
            sounds[key].addEventListener('error', handlers[key]);
        });
        return () => {
            Object.keys(sounds).forEach(key => {
                sounds[key].removeEventListener('error', handlers[key]);
            });
        };
    }, [sounds]);

    // Phasenmusik (inter/question/reveal): URL, Lautstärke, Überblendzeit aus musicConfig
    useEffect(() => {
        const phases = [
            { audio: sounds.inter,    cfg: musicConfig.inter },
            { audio: sounds.question, cfg: musicConfig.question },
            { audio: sounds.reveal,   cfg: musicConfig.reveal },
        ];

        const cleanups = phases.map(({ audio, cfg }) => {
            const resolvedUrl = resolveMediaUrl(cfg.url);
            if (audio.src !== resolvedUrl) {
                audio.pause();
                audio.src = resolvedUrl;
                audio.load();
            }
            audio.volume = cfg.volume;
            const shouldLoop = cfg.loop !== false;
            if (shouldLoop) {
                return setupLoopFade(audio, cfg.volume, cfg.fadeDuration);
            }
            audio.loop = false;
            return () => {};
        });

        return () => cleanups.forEach((c) => c());
    }, [sounds, musicConfig]);

    // Sound-Effekte: URL und Lautstärke aus soundEffectsConfig
    useEffect(() => {
        const map = {
            correct: sounds.correct,
            wrong:   sounds.wrong,
            select:  sounds.select,
        };
        Object.entries(map).forEach(([key, audio]) => {
            const cfg = soundEffectsConfig[key];
            if (!cfg) return;
            const url = resolveMediaUrl(cfg.url);
            if (audio.src !== url) {
                audio.src = url;
                audio.load();
            }
            audio.volume = cfg.volume;
        });
    }, [soundEffectsConfig, sounds]);

    // Finale-Audio: Lautstärke und Überblendzeit aus musicConfig
    useEffect(() => {
        const audio = finaleAudioRef.current;
        if (!audio) return undefined;
        const cfg = musicConfig[finaleConfigKey] || DEFAULT_MUSIC_CONFIG[finaleConfigKey];
        audio.volume = cfg.volume;
        return setupLoopFade(audio, cfg.volume, cfg.fadeDuration);
    }, [musicConfig, finaleConfigKey]);

    // Hintergrundmusik – Audio-Element initialisieren
    useEffect(() => {
        const audio = new Audio();
        bgMusicRef.current = audio;
        return () => {
            audio.pause();
            audio.src = '';
        };
    }, []);

    // ManualAudio – Audio-Element initialisieren
    useEffect(() => {
        const audio = new Audio();
        manualAudioRef.current = audio;
        return () => {
            audio.pause();
            audio.src = '';
        };
    }, []);

    // Audio-Konfiguration – Socket-Events
    useEffect(() => {
        const handleBgMusicState = (state) => setBgMusicState((prev) => ({ ...prev, ...state }));
        const handleMusicConfig = (cfg) => setMusicConfig((prev) => ({ ...prev, ...cfg }));
        const handleSoundEffects = (cfg) => setSoundEffectsConfig((prev) => ({ ...prev, ...cfg }));
        const handleManualAudio = (state) => setManualAudioState((prev) => ({ ...prev, ...state }));
        socket.on('bgMusic:state', handleBgMusicState);
        socket.on('musicConfig:update', handleMusicConfig);
        socket.on('soundEffects:update', handleSoundEffects);
        socket.on('manualAudio:state', handleManualAudio);
        return () => {
            socket.off('bgMusic:state', handleBgMusicState);
            socket.off('musicConfig:update', handleMusicConfig);
            socket.off('soundEffects:update', handleSoundEffects);
            socket.off('manualAudio:state', handleManualAudio);
        };
    }, []);

    // Hintergrundmusik – Wiedergabe steuern (mit optionalem Loop/Fade)
    useEffect(() => {
        const audio = bgMusicRef.current;
        if (!audio) return;

        const { url, volume, playing, fadeDuration = 1.0, loop = true } = bgMusicState;
        const resolvedUrl = url ? resolveMediaUrl(url) : '';

        if (audio.src !== resolvedUrl) {
            audio.pause();
            audio.currentTime = 0;
            audio.src = resolvedUrl;
            if (resolvedUrl) audio.load();
        }

        audio.volume = typeof volume === 'number' ? Math.max(0, Math.min(1, volume)) : 0.5;

        let cleanup = () => {};
        if (loop) {
            cleanup = setupLoopFade(audio, audio.volume, fadeDuration);
        } else {
            audio.loop = false;
        }

        if (playing && resolvedUrl) {
            audio.play().catch(() => {});
        } else {
            audio.pause();
            audio.currentTime = 0;
        }

        return cleanup;
    }, [bgMusicState]);

    // ManualAudio – Wiedergabe steuern (Fallback/Test, hat keinen Vorrang vor Phasenmusik)
    useEffect(() => {
        const audio = manualAudioRef.current;
        if (!audio) return;
        const { url, volume, playing, loop = false } = manualAudioState;
        const resolvedUrl = url ? resolveMediaUrl(url) : '';
        if (audio.src !== resolvedUrl) {
            audio.pause();
            audio.currentTime = 0;
            audio.src = resolvedUrl;
            if (resolvedUrl) audio.load();
        }
        audio.volume = typeof volume === 'number' ? Math.max(0, Math.min(1, volume)) : 0.5;
        let cleanup = () => {};
        if (loop) {
            cleanup = setupLoopFade(audio, audio.volume, 1.0);
        } else {
            audio.loop = false;
            // Nach Ende automatisch zurücksetzen (wichtig für One-Shot-Sounds)
            const handleEnded = () => socket.emit('manualAudio:set', { playing: false });
            audio.addEventListener('ended', handleEnded);
            cleanup = () => audio.removeEventListener('ended', handleEnded);
        }
        if (playing && resolvedUrl) {
            audio.play().catch(() => {});
        } else {
            audio.pause();
            audio.currentTime = 0;
        }
        return cleanup;
    }, [manualAudioState]);

    useEffect(() => {
        // 1) Wenn gar kein correctAnswerIndex => Sound zurücksetzen
        //    (z.B. neue Frage, oder noch keine richtige/falsche Antwort markiert)
        if (correctAnswerIndex == null) {
            // Wir können hier den „Flag“ wieder nullen
            setSoundPlayedForQuestion(null);
            return;
        }

        // 2) Wenn wir schon mal für diese Frage den Sound gespielt haben, Abbruch
        if (soundPlayedForQuestion === currentQuestion?.id) {
            // => Schon gespielt => Abbrechen
            return;
        }

        // 3) Prüfen, ob wir ein aktives Team + eine gewählte Antwort haben
        if (!activeTeamId) return;
        const chosenAnswer = selectedAnswers[activeTeamId];
        if (chosenAnswer === undefined) return;

        // => Wir sind sicher: currentQuestion hat correctAnswerIndex,
        //    activeTeamId hat chosenAnswer => jetzt Sound abspielen

        // Frage-Sound stoppen
        sounds.question.pause();
        sounds.question.currentTime = 0;

        if (chosenAnswer === correctAnswerIndex) {
            // Richtig
            const correctSounds = [sounds.correct /*, sounds.correct2 */];
            const chosenSound = correctSounds[Math.floor(Math.random() * correctSounds.length)];
            chosenSound.play().catch(err => {
                console.error('Fehler beim Abspielen correct:', err);
            });
        } else {
            // Falsch
            const wrongSounds = [sounds.wrong /*, sounds.wrong2 */];
            const chosenSound = wrongSounds[Math.floor(Math.random() * wrongSounds.length)];
            chosenSound.play().catch(err => {
                console.error('Fehler beim Abspielen wrong:', err);
            });
        }

        // 4) Merken, dass wir „für diese Question-ID“ den Sound gespielt haben
        setSoundPlayedForQuestion(currentQuestion?.id);

    }, [
        currentQuestion?.id,
        correctAnswerIndex,
        activeTeamId,
        selectedAnswers,
        sounds,
        soundPlayedForQuestion
    ]);

    // TeamId aus localStorage laden
    useEffect(() => {
        const storedTeamId = safeStorage.getItem('teamId');
        if (storedTeamId) {
            setYourTeamId(storedTeamId);
            console.log('[ShowPage] Geladene yourTeamId aus localStorage:', storedTeamId);
        } else {
            console.log('[ShowPage] Keine teamId in localStorage gefunden.');
        }
    }, []);

    // Fallback fuer Safari < 15.4 ohne :has()-Support: Body-Scroll-Sperre per Klasse
    // statt ausschliesslich per CSS-Selector (siehe ShowPage.css .show-page-active).
    useEffect(() => {
        document.body.classList.add('show-page-active');
        return () => {
            document.body.classList.remove('show-page-active');
        };
    }, []);

    // Eigenen Team-Beitrittscode laden (nur fuer das eigene Team sichtbar,
    // siehe getOwnTeamCode-Handler im Server). Erneut abrufen, wenn sich
    // yourTeamId aendert (Team erstellt/gewechselt) oder nach Reconnect.
    useEffect(() => {
        if (!playerId || !yourTeamId) {
            setMyTeamJoinCode(null);
            return;
        }
        socket.emit('getOwnTeamCode', { playerId }, (resp) => {
            setMyTeamJoinCode(resp?.ok ? resp.joinCode : null);
        });
    }, [playerId, yourTeamId]);

    // Race-freie Alternative fuer den Seiten-Reload-Fall: der obige Aufruf
    // feuert sofort beim Mount (yourTeamId kommt synchron aus localStorage),
    // moeglicherweise bevor der Server das playerRejoin fertig verarbeitet
    // und socketIdToPlayerId gesetzt hat - dann kommt getOwnTeamCode zu frueh
    // und liefert ok:false. loginSuccess (Antwort auf playerLogin/playerRejoin)
    // liefert den Code direkt mit und kann nicht zu frueh ankommen, weil der
    // Server ihn erst nach dem Setzen von socketIdToPlayerId sendet.
    useSocketEvent(socket, 'loginSuccess', useCallback(({ teamJoinCode } = {}) => {
        if (teamJoinCode !== undefined) {
            setMyTeamJoinCode(teamJoinCode);
        }
    }, []));

    useEffect(() => {
        const isOpen = besserwisserState?.isOpen === true;
        const currentQuestionId = besserwisserState?.questionId ?? null;

        if (!isOpen || currentQuestionId == null) {
            setBesserwisserSubmitted(false);
            setBesserwisserQuestionId(null);
            return;
        }

        if (
            besserwisserQuestionId == null ||
            String(currentQuestionId) !== String(besserwisserQuestionId)
        ) {
            setBesserwisserSubmitted(false);
            setBesserwisserQuestionId(currentQuestionId);
        }
    }, [besserwisserQuestionId, besserwisserState?.isOpen, besserwisserState?.questionId]);

    useEffect(() => {
        setTeamSharedText(yourTeamSharedText);
    }, [yourTeamSharedText, yourTeamId]);

    useEffect(() => {
        if (!hasMountedRef.current) {
            hasMountedRef.current = true;
            lastSeenTeamSharedTextRef.current = yourTeamSharedText;
            return;
        }

        if (isModeratorPanelOpen) {
            lastSeenTeamSharedTextRef.current = yourTeamSharedText;
            setHasUnreadTeamSharedText(false);
            return;
        }

        if (yourTeamSharedText !== lastSeenTeamSharedTextRef.current) {
            setHasUnreadTeamSharedText(true);
        }
    }, [isModeratorPanelOpen, yourTeamSharedText]);

    useEffect(() => {
        if (String(previousTeamIdRef.current) === String(yourTeamId)) return;

        setHasUnreadTeamSharedText(false);
        lastSeenTeamSharedTextRef.current = yourTeamSharedText;
        previousTeamIdRef.current = yourTeamId;
    }, [yourTeamId, yourTeamSharedText]);

    useEffect(() => {
        if (!currentTeam?.id) return undefined;
        if (teamSharedText === yourTeamSharedText) return undefined;

        const timeout = setTimeout(() => {
            fetchJson(`/api/teams/${currentTeam.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: currentTeam.name,
                    punkte: Number(currentTeam.punkte ?? 0),
                    sharedText: teamSharedText,
                }),
            }).catch((error) => {
                console.error('[ShowPage] Fehler beim Speichern vom Team-Text:', error);
            });
        }, TEAM_CHAT_SAVE_DELAY_MS);

        return () => clearTimeout(timeout);
    }, [currentTeam, teamSharedText, yourTeamSharedText]);

    const jokerQuestionId = currentQuestion?.id ?? null;
    const jokerUsedForActiveTeam =
        String(besserwisserState?.jokerUsedByTeamId?.[besserwisserState?.guessingTeamId] ?? '') === String(jokerQuestionId ?? '');

    useEffect(() => {
        setJokerBarsQuestionId(null);
    }, [jokerQuestionId]);

    useEffect(() => {
        if (!jokerQuestionId) {
            previousJokerUsedRef.current = jokerUsedForActiveTeam;
            return;
        }

        if (jokerUsedForActiveTeam && !previousJokerUsedRef.current && jokerBarsQuestionId == null) {
            setJokerBarsQuestionId(jokerQuestionId);
        }

        previousJokerUsedRef.current = jokerUsedForActiveTeam;
    }, [jokerBarsQuestionId, jokerQuestionId, jokerUsedForActiveTeam]);

    // Teams & Spieler laden
    useEffect(() => {
        fetchJson('/api/teams/')
            .then(data => setTeams(Array.isArray(data) ? data : []))
            .catch(err => console.error('Fehler beim Laden der Teams:', err));

        fetchJson('/api/players')
            .then(data => setAllPlayers(data))
            .catch(err => console.error('Fehler beim Laden aller Spieler:', err));
    }, []);

    useEffect(() => {
        // Zwischenphasen-Musik läuft auch, sobald ein "Finalfrage"-Finale verrechnet ist.
        const shouldPlayInterLoop = quizActive && !showQuestionContainer && !finaleMusicActive;
        const interAudio = sounds.inter;
        if (shouldPlayInterLoop) {
            if (interAudio.paused) {
                tryPlayAudio(interAudio, 'inter_loop');
            }
        } else {
            interAudio.pause();
            interAudio.currentTime = 0;
        }
    }, [finaleMusicActive, quizActive, showQuestionContainer, sounds.inter, tryPlayAudio]);

    useEffect(() => {
        const ensurePlayback = (audio, shouldPlay, label) => {
            if (shouldPlay) {
                if (audio.paused) {
                    tryPlayAudio(audio, label);
                }
            } else {
                audio.pause();
                audio.currentTime = 0;
            }
        };

        // Frage-Sound an/aus
        ensurePlayback(sounds.question, soundStatus.questionPlaying, 'question_loop');

        // Reveal-Sound an/aus
        ensurePlayback(sounds.reveal, soundStatus.revealPlaying, 'reveal_loop');
    }, [soundStatus, sounds.question, sounds.reveal, tryPlayAudio]);

    const attemptFinalePlayback = useCallback(() => {
        if (!finaleMusicActive) return;
        const audio = finaleAudioRef.current;
        if (!audio) return;
        tryPlayAudio(audio, 'finale_loop', {
            onBlocked: () => setFinaleAudioBlocked(true),
        }).then((started) => setFinaleAudioBlocked(!started));
    }, [finaleMusicActive, tryPlayAudio]);

    useEffect(() => {
        const audio = finaleAudioRef.current;
        if (!audio) return undefined;
        audio.volume = (musicConfig[finaleConfigKey] || DEFAULT_MUSIC_CONFIG[finaleConfigKey]).volume;
        if (finaleMusicActive) {
            attemptFinalePlayback();
        } else {
            audio.pause();
            audio.currentTime = 0;
            setFinaleAudioBlocked(false);
        }
        // KEIN Auto-Stop im Cleanup: die Musik läuft durch, bis finaleMusicActive
        // wirklich false wird (Finale beendet oder verrechnet) – nicht bei jedem Broadcast.
        return undefined;
    }, [finaleMusicActive, finaleConfigKey, musicConfig, attemptFinalePlayback]);

    const handleFinaleAudioClick = useCallback(() => {
        if (finaleMusicActive && finaleAudioBlocked) {
            attemptFinalePlayback();
        }
    }, [attemptFinalePlayback, finaleMusicActive, finaleAudioBlocked]);

    // --------------------------------------
    // Socket-Events
    // --------------------------------------
    const requestInitialState = useCallback(() => {
        socket.emit('getQuizState');
        socket.emit('finale:state:request');
        console.log('[ShowPage] request initial quizState');
    }, []);

    useEffect(() => {
        if (socket.connected) {
            requestInitialState();
        }
    }, [requestInitialState]);

    useSocketEvent(socket, 'connect', requestInitialState);

    const handleQuestionSelected = useCallback((question) => {
        console.log('[ShowPage] questionSelected =>', question);
        const normalizedQuestion = question ? fromServerQuestion(question) : null;
        setCountdown(null);
        setInitialCountdown(null);
        setCountdownDurationMs(null);
        setCountdownEndsAtMs(null);
        setCurrentQuestion(normalizedQuestion);
        setSelectedMatrixCellId(normalizedQuestion?.id || null);
        setShowQuestionContainer(false);
        setDisplayedQuestion(false);
        setDisplayedAnswers([]);
        setSelectedAnswer(null);
        setCorrectAnswerIndex(null);
        setSelectedAnswers({});
        setShowPredictions(false);
        setAllPredictions({});
        setMyTeamPrediction(null);
        setPredictionsRevealed(false);
        setPredictionDurationMs(null);
        setPredictionEndsAtMs(null);
    }, []);

    const handleQuestionClicked = useCallback(({ frage }) => {
        console.log('[ShowPage] questionClicked => ', frage);
        setDisplayedQuestion(true);
        setShowQuestionContainer(true);
        setSoundStatus({
            questionPlaying: true,
            revealPlaying: false,
        });
    }, []);

    const handleAnswerClicked = useCallback(({ frage, antwort }) => {
        console.log('[ShowPage] answerClicked => ', frage, antwort);
        setDisplayedAnswers(prev => [...prev, antwort]);
    }, []);

    const handleAnswerSelected = useCallback(({ teamId, answerIndex }) => {
        console.log('[ShowPage] answerSelected => ', teamId, answerIndex);
        setSelectedAnswers(prev => ({
            ...prev,
            [teamId]: answerIndex,
        }));
        sounds.select.play();
    }, [sounds.select]);

    const handleCorrectAnswer = useCallback(({ correctAnswerIndex }) => {
        if (typeof correctAnswerIndex === 'number') {
            console.log('[ShowPage] correctAnswer =>', correctAnswerIndex);
            setCorrectAnswerIndex(correctAnswerIndex);
            setSoundStatus(prev => ({
                ...prev,
                questionPlaying: false,
            }));
        } else {
            console.error('[ShowPage] correctAnswer: Ungültiger Index:', correctAnswerIndex);
        }
    }, []);

    const handleCountdownStarted = useCallback(({ timeLeft }) => {
        const adjustedTimeLeft = Math.max(timeLeft - 1, 0);
        setInitialCountdown(adjustedTimeLeft);
        setCountdown(adjustedTimeLeft);
        const durationMs = adjustedTimeLeft * 1000;
        setCountdownDurationMs(durationMs);
        setCountdownEndsAtMs(Date.now() + durationMs);
    }, []);

    const handleCountdownTick = useCallback(({ timeLeft }) => {
        const adjustedTimeLeft = Math.max(timeLeft - 1, 0);
        setCountdown(adjustedTimeLeft);
        setCountdownEndsAtMs(prev => {
            const expectedEndsAt = Date.now() + adjustedTimeLeft * 1000;
            if (prev == null) return expectedEndsAt;
            const drift = expectedEndsAt - prev;
            if (Math.abs(drift) > 200) {
                return prev + drift * 0.5;
            }
            return prev;
        });
    }, []);

    const handleCountdownEnded = useCallback(() => {
        setCountdown(null);
        setInitialCountdown(null);
        setCountdownDurationMs(null);
        setCountdownEndsAtMs(null);
        setSoundStatus(prev => ({
            ...prev,
            questionPlaying: false,
        }));
    }, []);

    const handlePredictionStarted = useCallback(({ timeLeft }) => {
        const adjustedTimeLeft = Math.max(timeLeft - 1, 0);
        setInitialPredictionCountdown(adjustedTimeLeft);
        setPredictionCountdown(adjustedTimeLeft);
        const durationMs = adjustedTimeLeft * 1000;
        setPredictionDurationMs(durationMs);
        setPredictionEndsAtMs(Date.now() + durationMs);
        setMyTeamPrediction(null);
        setPredictionActive(true);
        setIsPredictionDrawerOpen(true);
    }, []);

    const handlePredictionUpdated = useCallback(({ teamId, willBeCorrect }) => {
        setAllPredictions(prev => ({
            ...prev,
            [teamId]: willBeCorrect,
        }));
        if (String(teamId) === String(yourTeamId)) {
            setMyTeamPrediction(willBeCorrect);
        }
    }, [yourTeamId]);

    const handlePredictionTick = useCallback(({ timeLeft }) => {
        const adjustedTimeLeft = Math.max(timeLeft - 1, 0);
        setPredictionCountdown(adjustedTimeLeft);
        setPredictionEndsAtMs(prev => {
            const expectedEndsAt = Date.now() + adjustedTimeLeft * 1000;
            if (prev == null) return expectedEndsAt;
            const drift = expectedEndsAt - prev;
            if (Math.abs(drift) > 200) {
                return prev + drift * 0.5;
            }
            return prev;
        });
    }, []);

    const handlePredictionEnded = useCallback(() => {
        setPredictionCountdown(null);
        setInitialPredictionCountdown(null);
        setPredictionDurationMs(null);
        setPredictionEndsAtMs(null);
        setPredictionActive(false);
        setIsPredictionDrawerOpen(false);
    }, []);

    const handlePredictionsRevealed = useCallback(({ predictionAnswers }) => {
        setShowPredictions(true);
        setAllPredictions(predictionAnswers);
    }, []);

    // Setzt den passenden Finale-State und behält die Referenz bei unveränderten
    // Payloads (kein Re-Render / kein Musik-Neustart pro Broadcast).
    const applyFinalePayload = useCallback((payload) => {
        const keep = (prev) => (sameJson(prev, payload) ? prev : payload);
        if (payload && payload.kind === 'quizpoker') {
            setQuizpokerFinale(keep);
            setFinalfrageFinale(null);
            setFinale(null);
        } else if (payload && payload.kind === 'finalfrage') {
            setFinalfrageFinale(keep);
            setQuizpokerFinale(null);
            setFinale(null);
        } else {
            setFinale(normalizeFinaleState(payload || null));
            setQuizpokerFinale(null);
            setFinalfrageFinale(null);
        }
    }, []);

    const handleQuizState = useCallback((srvState) => {
        console.log('[ShowPage] quizState empfangen:', srvState);
        const {
            quizActive: srvQuizActive,
            paused: srvPaused,
            currentQuestion: srvQ,
            activeTeamId: srvActiveTeamId,
            selectedAnswers: srvSelectedAnswers,
            questionRevealed: srvRevealed,
            revealedAnswers: srvRevealedAnswers,
            highlightedAnswerIndex,
            predictionActive: srvPredictionActive,
            predictionAnswers: srvPredictionAnswers,
            predictionsRevealed,
            countdown: srvCountdown,
            predictionCountdown: srvPredictionCountdown,
            soundStatus: srvSoundStatus = {},
            bgMusic: srvBgMusic,
            matrixVisible: srvMatrixVisible,
            matrix: srvMatrix,
            finale: srvFinale,
            besserwisser: srvBesserwisser,
        } = srvState;

        // Audio-Configs nur bei echter Aenderung neu setzen – sonst startet die
        // Hintergrundmusik bei jedem Broadcast neu (identische Referenz => keine Effekt-Re-Runs).
        if (srvBgMusic) {
            setBgMusicState((prev) => {
                const next = { ...prev, ...srvBgMusic };
                return sameJson(prev, next) ? prev : next;
            });
        }
        if (srvState.manualAudio) {
            setManualAudioState((prev) => {
                const next = { ...prev, ...srvState.manualAudio };
                return sameJson(prev, next) ? prev : next;
            });
        }
        if (srvState.musicConfig) {
            setMusicConfig((prev) => {
                const next = { ...prev, ...srvState.musicConfig };
                return sameJson(prev, next) ? prev : next;
            });
        }
        if (srvState.soundEffectsConfig) {
            setSoundEffectsConfig((prev) => {
                const next = { ...prev, ...srvState.soundEffectsConfig };
                return sameJson(prev, next) ? prev : next;
            });
        }

        const normalizedQuestion = srvQ ? fromServerQuestion(srvQ) : null;
        setQuizActive(srvQuizActive);
        setPaused(srvPaused);
        setCurrentQuestion(normalizedQuestion);
        setDisplayedQuestion(!!srvRevealed);
        setDisplayedAnswers(srvRevealedAnswers || []);
        setActiveTeamId(srvActiveTeamId != null ? String(srvActiveTeamId) : null);
        setSelectedAnswers(srvSelectedAnswers || {});
        setCorrectAnswerIndex(highlightedAnswerIndex ?? null);

        setPredictionActive(srvPredictionActive);
        setPredictionAnswers(srvPredictionAnswers || {});
        setShowPredictions(predictionsRevealed || false);
        setPredictionsRevealed(predictionsRevealed || false);
        setIsPredictionDrawerOpen(!!srvPredictionActive && srvPredictionCountdown != null);
        setBesserwisserState(srvBesserwisser ?? null);

        setSelectedMatrixCellId(srvState.matrixSelectedCellId || null);

        // selectedAnswer immer serverautoritativ aus srvSelectedAnswers ableiten,
        // statt es nur bei fehlender Frage zurückzusetzen. Sonst kann ein Resync
        // während einer laufenden Frage (Reconnect, Tab-Wechsel) eine bereits
        // lokal gesetzte, vom Server nicht bestätigte Auswahl überleben lassen
        // und Klicks dauerhaft sperren.
        const myServerAnswer = yourTeamId != null ? (srvSelectedAnswers || {})[yourTeamId] : undefined;
        setSelectedAnswer(myServerAnswer !== undefined ? myServerAnswer : null);

        if (!normalizedQuestion) {
            setCountdown(null);
            setInitialCountdown(null);
            setDisplayedQuestion(false);
            setDisplayedAnswers([]);
        }
        setShowQuestionContainer(!!srvState.questionRevealed);

        if (srvCountdown) {
            const syncedInitial = Math.max((srvCountdown.duration ?? srvCountdown.timeLeft) - 1, 0);
            const syncedRemaining = Math.max(srvCountdown.timeLeft - 1, 0);
            setInitialCountdown(syncedInitial);
            setCountdown(syncedRemaining);
            const durationMs = syncedInitial * 1000;
            setCountdownDurationMs(durationMs);
            setCountdownEndsAtMs(Date.now() + syncedRemaining * 1000);
        } else if (srvCountdown === null) {
            setCountdown(null);
            setInitialCountdown(null);
            setCountdownDurationMs(null);
            setCountdownEndsAtMs(null);
        }

        if (srvPredictionCountdown) {
            const syncedInitial = Math.max((srvPredictionCountdown.duration ?? srvPredictionCountdown.timeLeft) - 1, 0);
            const syncedRemaining = Math.max(srvPredictionCountdown.timeLeft - 1, 0);
            setInitialPredictionCountdown(syncedInitial);
            setPredictionCountdown(syncedRemaining);
            const durationMs = syncedInitial * 1000;
            setPredictionDurationMs(durationMs);
            setPredictionEndsAtMs(Date.now() + syncedRemaining * 1000);
        } else if (srvPredictionCountdown === null) {
            setPredictionCountdown(null);
            setInitialPredictionCountdown(null);
            setPredictionDurationMs(null);
            setPredictionEndsAtMs(null);
        }

        setSoundStatus({
            questionPlaying: !!srvSoundStatus.questionPlaying,
            revealPlaying: !!srvSoundStatus.revealPlaying,
        });

        setMatrixVisible(!!srvMatrixVisible);

        if (srvMatrixVisible && srvMatrix) {
            setMatrixData(srvMatrix);
        }

        if (srvFinale !== undefined) {
            applyFinalePayload(srvFinale);
        }
    }, [yourTeamId, applyFinalePayload]);

    const handleFinaleState = useCallback(({ finale: finaleState }) => {
        applyFinalePayload(finaleState || null);
    }, [applyFinalePayload]);

    const handleTeamsUpdated = useCallback((updatedTeam) => {
        console.debug('[ShowPage][teamsUpdated]', updatedTeam);
        setTeams((prev) =>
            prev.map((team) =>
                String(team.id) === String(updatedTeam.id) ? { ...team, ...updatedTeam } : team
            )
        );
        if (updatedTeam?.spieler) {
            setAllPlayers((prevPlayers) =>
                prevPlayers.map((player) => {
                    const found = updatedTeam.spieler.find((entry) => entry.id === player.id);
                    return found ? { ...player, team: found.team } : player;
                })
            );
        }
    }, []);

    const handleTeamCreated = useCallback((newTeam) => {
        setTeams(prev => [...prev, newTeam]);
    }, []);

    const handleTeamDeleted = useCallback(({ teamId }) => {
        setTeams(prev => prev.filter(t => t.id !== teamId));
        setAllPlayers(prev =>
            prev.map(player =>
                getTeamIdFromPlayer(player) === teamId
                    ? { ...player, team: null, teamId: null }
                    : player
            )
        );
    }, []);

    const handlePlayerAdded = useCallback((newPlayer) => {
        setAllPlayers((prev) => {
            const exists = prev.some((player) => player.id === newPlayer.id);
            if (exists) {
                return prev.map((player) =>
                    player.id === newPlayer.id ? newPlayer : player
                );
            }
            return [...prev, newPlayer];
        });

        const playerTeamId = getTeamIdFromPlayer(newPlayer);
        setTeams((prev) =>
            prev.map((team) => {
                const containsPlayer = team.spieler.some(
                    (sp) => sp.id === newPlayer.id
                );
                if (team.id === playerTeamId) {
                    const updatedRoster = containsPlayer
                        ? team.spieler.map((sp) =>
                              sp.id === newPlayer.id ? newPlayer : sp
                          )
                        : [...team.spieler, newPlayer];
                    return { ...team, spieler: updatedRoster };
                }

                if (containsPlayer) {
                    return {
                        ...team,
                        spieler: team.spieler.filter(
                            (sp) => sp.id !== newPlayer.id
                        ),
                    };
                }

                return team;
            })
        );
    }, []);

    const handlePlayerUpdated = useCallback((updatedPlayer) => {
        setAllPlayers((prev) =>
            prev.map((p) => (p.id === updatedPlayer.id ? updatedPlayer : p))
        );

        const updatedTeamId = getTeamIdFromPlayer(updatedPlayer);

        setTeams((prevTeams) =>
            prevTeams.map((team) => {
                const containsPlayer = team.spieler.some(
                    (sp) => sp.id === updatedPlayer.id
                );
                if (team.id === updatedTeamId) {
                    const updatedRoster = containsPlayer
                        ? team.spieler.map((sp) =>
                              sp.id === updatedPlayer.id ? updatedPlayer : sp
                          )
                        : [...team.spieler, updatedPlayer];
                    return { ...team, spieler: updatedRoster };
                }

                if (containsPlayer) {
                    return {
                        ...team,
                        spieler: team.spieler.filter(
                            (sp) => sp.id !== updatedPlayer.id
                        ),
                    };
                }

                return team;
            })
        );

        const myPlayerId = safeStorage.getItem('playerId');
        if (myPlayerId && String(updatedPlayer.id) === String(myPlayerId)) {
            if (updatedTeamId != null) {
                const normalizedTeamId = String(updatedTeamId);
                safeStorage.setItem('teamId', normalizedTeamId);
                setYourTeamId(normalizedTeamId);
                console.log('[ShowPage] Eigene Team-Zuordnung aktualisiert:', {
                    playerId: myPlayerId,
                    teamId: normalizedTeamId,
                });
            } else {
                safeStorage.removeItem('teamId');
                setYourTeamId(null);
                console.log('[ShowPage] Eigene Team-Zuordnung entfernt:', { playerId: myPlayerId });
            }
        }
    }, []);

    const handlePlayerDeleted = useCallback(({ playerId }) => {
        setAllPlayers(prev => prev.filter(p => p.id !== playerId));
        setTeams(prev =>
            prev.map(t => ({
                ...t,
                spieler: t.spieler.filter(sp => sp.id !== playerId),
            }))
        );
    }, []);

    const handleQuizEnded = useCallback(() => {
        setQuizActive(false);
        setPaused(false);
        setCurrentQuestion(null);
        setDisplayedQuestion(false);
        setDisplayedAnswers([]);
        setCountdown(null);
        setInitialCountdown(null);
        setCountdownDurationMs(null);
        setCountdownEndsAtMs(null);
        setPredictionCountdown(null);
        setInitialPredictionCountdown(null);
        setPredictionDurationMs(null);
        setPredictionEndsAtMs(null);
        setSelectedAnswer(null);
        setCorrectAnswerIndex(null);
        setActiveTeamId(null);

        setShowPredictions(false);
        setAllPredictions({});
        setMyTeamPrediction(null);

        setPredictionActive(false);
        setPredictionAnswers({});
        setBesserwisserState(null);
        setBesserwisserSubmitted(false);
        setBesserwisserQuestionId(null);
    }, []);

    const handleErrorMessage = useCallback((payload) => {
        if (!payload) return;
        const message = typeof payload === 'string'
            ? payload
            : payload.message || payload.msg || payload.error;
        console.error('[ShowPage] errorMessage:', message || payload);
        alert(message || 'Unbekannter Fehler');
    }, []);

    const handleMatrixState = useCallback(({ visible, matrix }) => {
        console.log('[ShowPage] matrixState => visible:', visible);
        if (visible) {
            setMatrixVisible(true);
            if (matrix) {
                setMatrixData(matrix);
            }
        } else {
            setMatrixVisible(false);
        }
    }, []);

    useSocketEvent(socket, 'questionSelected', handleQuestionSelected);
    useSocketEvent(socket, 'questionClicked', handleQuestionClicked);
    useSocketEvent(socket, 'answerClicked', handleAnswerClicked);
    useSocketEvent(socket, 'answerSelected', handleAnswerSelected);
    useSocketEvent(socket, 'correctAnswer', handleCorrectAnswer);
    useSocketEvent(socket, 'countdownStarted', handleCountdownStarted);
    useSocketEvent(socket, 'countdownTick', handleCountdownTick);
    useSocketEvent(socket, 'countdownEnded', handleCountdownEnded);
    useSocketEvent(socket, 'predictionStarted', handlePredictionStarted);
    useSocketEvent(socket, 'predictionUpdated', handlePredictionUpdated);
    useSocketEvent(socket, 'predictionTick', handlePredictionTick);
    useSocketEvent(socket, 'predictionEnded', handlePredictionEnded);
    useSocketEvent(socket, 'predictionsRevealed', handlePredictionsRevealed);
    useSocketEvent(socket, 'quizState', handleQuizState);
    useSocketEvent(socket, 'finale:state', handleFinaleState);
    useSocketEvent(socket, 'teamsUpdated', handleTeamsUpdated);
    useSocketEvent(socket, 'teamCreated', handleTeamCreated);
    useSocketEvent(socket, 'teamDeleted', handleTeamDeleted);
    useSocketEvent(socket, 'playerAdded', handlePlayerAdded);
    useSocketEvent(socket, 'playerUpdated', handlePlayerUpdated);
    useSocketEvent(socket, 'playerDeleted', handlePlayerDeleted);
    useSocketEvent(socket, 'quizEnded', handleQuizEnded);
    useSocketEvent(socket, 'errorMessage', handleErrorMessage);
    useSocketEvent(socket, 'matrixState', handleMatrixState);

    // --------------------------------------
    // NEU: Matrix-Ein/Ausblend-Logik
    // --------------------------------------
    useEffect(() => {
        // Falls (noch) keine Daten da sind, können wir nichts animieren:
        if (!matrixData) return;

        // 1) EINBLENDEN => Nur dann, wenn matrixVisible===true, aber showMatrix===false
        if (matrixVisible && !showMatrix) {
            // Wir setzen showMatrix auf true => Matrix wird gerendert
            setShowMatrix(true);
            setMatrixAnimationStage('hidden');
            setTimeout(() => {
                setMatrixAnimationStage('show');
            }, 0);

            // Alle Zellen sammeln:
            const cells = [];
            matrixData.forEach((row, rIndex) => {
                row.forEach((cell) => {
                    if (cell) {
                        cells.push({
                            id: cell.id,
                            row: rIndex
                        });
                    }
                });
            });

            // Shuffle für "popIn"-Reihenfolge
            function shuffle(arr) {
                return arr
                    .map((item) => ({ sort: Math.random(), value: item }))
                    .sort((a, b) => a.sort - b.sort)
                    .map((obj) => obj.value);
            }
            const shuffled = shuffle(cells);

            // 1a) Delays-Objekt anlegen
            const delays = {};

            // Pop-In-Index (Zufällige Reihenfolge)
            shuffled.forEach((cell, i) => {
                if (!delays[cell.id]) delays[cell.id] = {};
                delays[cell.id].inIndex = i;
            });

            // 1b) Auch gleich die outIndex festlegen (fürs spätere Ausblenden)
            //    => "von unten nach oben" = höchste Zeilennummer zuerst
            const rowGroups = {};
            cells.forEach(cell => {
                if (!rowGroups[cell.row]) {
                    rowGroups[cell.row] = [];
                }
                rowGroups[cell.row].push(cell.id);
            });
            // Sortiere rows absteigend
            const sortedRows = Object.keys(rowGroups)
                .map(r => parseInt(r, 10))
                .sort((a, b) => b - a);

            let outIndexCounter = 0;
            for (const row of sortedRows) {
                const cellIdsInRow = rowGroups[row];
                for (const id of cellIdsInRow) {
                    if (!delays[id]) delays[id] = {};
                    delays[id].outIndex = outIndexCounter;
                }
                outIndexCounter++;
            }

            // Ergebnis in den State
            setMatrixDelays(delays);

            // 1c) Zeit berechnen, bis alle "in"-Animationen fertig sind:
            const maxInIndex = shuffled.length - 1;
            const totalInTime = (maxInIndex * 0.03 + 0.25) * 1000;

            // Nach Ende der Einblend-Animation => "idle"
            setTimeout(() => {
                setMatrixAnimationStage('idle');
            }, totalInTime);

            // 2) AUSBLENDEN => Nur dann, wenn matrixVisible===false, aber showMatrix===true
        } else if (!matrixVisible && showMatrix) {
            setMatrixAnimationStage('exit');

            // Ermittle größten outIndex
            let maxOutIndex = 0;
            Object.values(matrixDelays).forEach((obj) => {
                if (obj.outIndex > maxOutIndex) {
                    maxOutIndex = obj.outIndex;
                }
            });

            // 0.05s pro Index + 0.5s Animationsdauer
            const totalOutTime = (maxOutIndex * 0.02 + 0.22) * 1000;

            // Nach Ablauf der "out"-Animation => Matrix ausblenden
            setTimeout(() => {
                setShowMatrix(false);
                // Falls du nach dem Ausblenden die Daten noch löschen willst:
                // setMatrixData(null);
            }, totalOutTime);
        }

        // Falls matrixVisible===true & showMatrix===true => nichts tun
        // Falls matrixVisible===false & showMatrix===false => nichts tun

    }, [matrixVisible, showMatrix, matrixData, matrixDelays]);

    const normalizedAttachments = useMemo(
        () => normalizeAttachments(currentQuestion?.attachments || [], currentQuestion?.id),
        [currentQuestion]
    );

    const attachmentsWithState = useMemo(
        () =>
            normalizedAttachments.map((att) => ({
                attachment: att,
                ctrl: getAttachmentState(currentQuestion?.id, att),
            })),
        [currentQuestion?.id, getAttachmentState, normalizedAttachments]
    );

    const overlayEntry = useMemo(() => {
        return (
            attachmentsWithState
                .filter(({ ctrl }) => ctrl.visible && (ctrl.position || 'above') === 'overlay')
                .sort((a, b) => (b.ctrl.updatedAt || 0) - (a.ctrl.updatedAt || 0))[0] || null
        );
    }, [attachmentsWithState]);

    const rightEntry = useMemo(() => {
        return (
            attachmentsWithState
                .filter(({ ctrl }) => ctrl.visible && (ctrl.position || 'above') === 'right')
                .sort((a, b) => (b.ctrl.updatedAt || 0) - (a.ctrl.updatedAt || 0))[0] || null
        );
    }, [attachmentsWithState]);

    const aboveEntry = useMemo(() => {
        return (
            attachmentsWithState
                .filter(({ ctrl }) => ctrl.visible && (ctrl.position || 'above') === 'above')
                .sort((a, b) => (b.ctrl.updatedAt || 0) - (a.ctrl.updatedAt || 0))[0] || null
        );
    }, [attachmentsWithState]);

    const inlineEntry = overlayEntry ? null : rightEntry || aboveEntry;


    // --------------------------------------
    // Prediction
    // --------------------------------------
    function handlePredictAnswer(willBeCorrect) {
        const myTeam = safeStorage.getItem('teamId');
        if (!myTeam || String(myTeam) === String(activeTeamId)) return;
        if (!predictionCountdown || predictionCountdown <= 0) return;

        setMyTeamPrediction(willBeCorrect);
        socket.emit('predictAnswer', { teamId: myTeam, willBeCorrect });
    }

    // --------------------------------------
    // handleSelectAnswer
    // --------------------------------------
    function handleSelectAnswer(answerIndex) {
        if (!activeTeamId || String(yourTeamId) !== String(activeTeamId)) {
            console.log('[ShowPage] Nicht das aktive Team!');
            return;
        }
        if (countdown === null || countdown <= 0) {
            console.log('[ShowPage] Countdown abgelaufen oder nicht gestartet.');
            return;
        }
        if (selectedAnswer !== null) {
            console.log('[ShowPage] Bereits eine Antwort gewählt:', selectedAnswer);
            return;
        }

        sounds.select.play();
        setSoundStatus(prev => ({
            ...prev,
            questionPlaying: false,
            revealPlaying: true,
        }));

        setSelectedAnswer(answerIndex);
        // `pauseCountdown` würde serverseitig ggf. den Besserwisser-State löschen.
        // Deshalb stoppen wir den Countdown nur mit Preserve-Flag vor der Antwort.
        socket.emit('pauseCountdown', { preserveBesserwisser: true });
        sounds.question.pause();
        sounds.question.currentTime = 0;
        socket.emit('selectAnswer', { teamId: String(yourTeamId), answerIndex });
    }

    function handleBesserwisserSubmit(answerIndex) {
        const questionId = besserwisserState?.questionId;
        const guessingTeamId = besserwisserState?.guessingTeamId;
        if (!besserwisserState?.isOpen || !questionId) return;
        if (!yourTeamId || String(guessingTeamId) === String(yourTeamId)) return;
        if (countdown === null || countdown <= 0) return;
        if (besserwisserSubmitted) return;
        const option = String.fromCharCode(65 + answerIndex);
        if (!['A', 'B', 'C', 'D'].includes(option)) return;

        socket.emit('besserwisser:submit', { questionId, option }, (resp) => {
            if (resp?.ok) {
                setBesserwisserSubmitted(true);
                return;
            }
            alert(resp?.error || resp?.message || 'Fehler beim Senden der Antwort.');
        });
    }

    // --------------------------------------
    // renderAnswers
    // --------------------------------------
    function renderAnswers() {
        if (!currentQuestion || !displayedQuestion) return null;
        const besserwisserOpen = besserwisserState?.isOpen === true;
        const guessingTeamId = besserwisserState?.guessingTeamId;
        const isGuessingTeam =
            guessingTeamId != null &&
            yourTeamId != null &&
            String(guessingTeamId) === String(yourTeamId);
        const answersByTeamId = besserwisserState?.answersByTeamId || {};
        const jokerDistribution = Object.values(answersByTeamId).reduce(
            (acc, entry) => {
                if (!entry) return acc;
                if (guessingTeamId != null && String(entry.teamId) === String(guessingTeamId)) return acc;
                if (!['A', 'B', 'C', 'D'].includes(entry.option)) return acc;
                acc.counts[entry.option] += 1;
                acc.total += 1;
                return acc;
            },
            { counts: { A: 0, B: 0, C: 0, D: 0 }, total: 0 }
        );
        const shouldShowJokerDistribution =
            guessingTeamId != null &&
            jokerBarsQuestionId != null &&
            String(jokerBarsQuestionId) === String(jokerQuestionId);
        const besserwisserAnswer = besserwisserState?.answersByTeamId?.[yourTeamId];
        const hasBesserwisserAnswer = !!besserwisserAnswer;
        const canBesserwisserSubmit =
            besserwisserOpen &&
            !!yourTeamId &&
            !isGuessingTeam &&
            countdown !== null &&
            countdown > 0 &&
            !besserwisserSubmitted &&
            !hasBesserwisserAnswer;
        const shouldShowBesserwisserStatus = besserwisserSubmitted || hasBesserwisserAnswer;
        const besserwisserTime =
            hasBesserwisserAnswer && Number.isFinite(besserwisserAnswer?.deltaMs)
                ? formatDeltaMs(besserwisserAnswer.deltaMs)
                : null;
        const canClick = (
            activeTeamId !== null &&
            String(activeTeamId) === String(yourTeamId) &&
            countdown > 0 &&
            selectedAnswer === null
        );
        const canBesserwisserClick = !canClick && canBesserwisserSubmit;
        const anyClickAllowed = canClick || canBesserwisserClick;
        const showLockOverlay = yourTeamId != null && !anyClickAllowed && correctAnswerIndex === null;
        // Eigener Klick-Kreis: nur das eigene Team soll sehen, was es selbst
        // angeklickt hat (als aktives Team oder als Besserwisser-Rateteam) -
        // niemals die Auswahl eines anderen Teams.
        const isActiveTeamViewer = activeTeamId !== null && String(activeTeamId) === String(yourTeamId);
        const besserwisserAnswerIndex = besserwisserAnswer
            ? ['A', 'B', 'C', 'D'].indexOf(besserwisserAnswer.option)
            : -1;
        const myPickIndex = isActiveTeamViewer ? selectedAnswer : besserwisserAnswerIndex;

        return (
            <>
                <div className="answer-list-wrap">
                    <ul className="answer-list">
                    {displayedAnswers.map((ans, idx) => {
                        const isCorrect = (idx === correctAnswerIndex);
                        const isSelectedByActiveTeam = (
                            activeTeamId && selectedAnswers[activeTeamId] === idx
                        );
                        const isMyOwnPick = myPickIndex === idx;
                        const optionLetter = String.fromCharCode(65 + idx);
                        const distributionCount = jokerDistribution.counts[optionLetter] || 0;
                        const distributionPercent = jokerDistribution.total
                            ? (distributionCount / jokerDistribution.total) * 100
                            : 0;

                        let classNames = [];
                        if (correctAnswerIndex !== null) {
                            if (isCorrect) {
                                classNames.push('korrekt');
                            }
                            if (isSelectedByActiveTeam && !isCorrect) {
                                classNames.push('falsch');
                            }
                        } else {
                            if (isSelectedByActiveTeam) {
                                classNames.push('ausgewaehlt');
                            }
                        }
                        if (canClick || canBesserwisserClick) {
                            classNames.push('clickable');
                        } else {
                            classNames.push('locked');
                        }

                        return (
                            <li
                                key={idx}
                                className={classNames.join(' ')}
                                onClick={() => {
                                    if (canClick) {
                                        handleSelectAnswer(idx);
                                    } else if (canBesserwisserClick) {
                                        handleBesserwisserSubmit(idx);
                                    }
                                }}
                            >
                                {shouldShowJokerDistribution && (
                                    <span
                                        className="answer-joker-bar"
                                        style={{ width: `${distributionPercent}%` }}
                                        aria-hidden="true"
                                    />
                                )}
                                <span className={`answer-option${(canClick || canBesserwisserClick || isMyOwnPick) ? ' highlight' : ''}`}>
                                    {optionLetter}
                                </span>
                                <span className="answer-text">{ans}</span>
                            </li>
                        );
                    })}
                    </ul>
                    {showLockOverlay && (
                        <div className="answer-lock-overlay" aria-hidden="true" />
                    )}
                </div>
                {shouldShowBesserwisserStatus && (
                    <div className="besserwisser-status">
                        <span>Gesendet</span>
                        {besserwisserTime && (
                            <span className="besserwisser-time">{besserwisserTime}</span>
                        )}
                    </div>
                )}
            </>
        );
    }

    // --------------------------------------
    // Matrix UI
    // --------------------------------------
    const matrixContainerRef = useRef(null);
    const [matrixContainerWidth, setMatrixContainerWidth] = useState(0);

    useEffect(() => {
        if (!matrixContainerRef.current) return;

        const updateWidth = () => {
            const width = matrixContainerRef.current?.getBoundingClientRect().width || 0;
            setMatrixContainerWidth(width);
        };

        updateWidth();

        if (typeof ResizeObserver === 'undefined') {
            return undefined;
        }

        const observer = new ResizeObserver(() => updateWidth());
        observer.observe(matrixContainerRef.current);

        return () => observer.disconnect();
    }, [showMatrix]);

    useEffect(() => {
        const phase = finale?.phase || null;
        if (phase === 'reveal' && previousFinalePhase.current !== 'reveal') {
            setFinaleRevealToken((prev) => prev + 1);
        }
        previousFinalePhase.current = phase;
    }, [finale?.phase]);

    const matrixLayout = useMemo(() => {
        const rowCount = matrixData?.length || 0;
        const colCount = matrixData ? Math.max(0, ...matrixData.map(row => row.length)) : 0;
        const cellSize = 90;
        const cellGap = 8;
        const containerPadding = 24;
        const matrixWidth = colCount ? colCount * cellSize + (colCount - 1) * cellGap + containerPadding : 0;
        const scale = matrixWidth > 0 && matrixContainerWidth > 0 ? matrixContainerWidth / matrixWidth : 1;
        const invalidLayout = !rowCount || !colCount || !Number.isFinite(scale);
        const scaleTooSmall = scale < 0.75;
        const containerTooSmall = matrixWidth > matrixContainerWidth && matrixContainerWidth > 0;
        // Fallback triggers when scale is too small, container is too narrow, or layout values are invalid.
        const useCompactLayout = invalidLayout || scaleTooSmall || containerTooSmall;

        return { colCount, useCompactLayout };
    }, [matrixData, matrixContainerWidth]);

    function handleMatrixCellClick(cell) {
        if (activeTeamId === null || String(yourTeamId) !== String(activeTeamId)) return;
        if (cell.played) return;
        setSelectedMatrixCellId(cell.id);
        socket.emit('matrixQuestionSelected', { questionId: cell.id }, resp => {
            if (!resp?.success) {
                alert(resp?.message || 'Fehler bei der Auswahl der Matrixfrage.');
            }
        });
    }

    function renderMatrix() {
        // Erst anzeigen, wenn showMatrix=true und matrixData != null
        if (!showMatrix || !matrixData) return null;

        const { colCount, useCompactLayout } = matrixLayout;

        const isActiveTeam = activeTeamId !== null && String(yourTeamId) === String(activeTeamId);

        return (
            <MatrixGrid
                matrix={matrixData}
                colCount={colCount}
                useCompactLayout={useCompactLayout}
                animated
                interactive
                stage={matrixAnimationStage}
                cellDelays={matrixDelays}
                selectedCellId={selectedMatrixCellId}
                canClick={(cell) => isActiveTeam && !cell.played}
                onCellClick={handleMatrixCellClick}
                containerRef={matrixContainerRef}
            />
        );
    }

    const predictionDrawerVisible =
        predictionActive &&
        predictionCountdown !== null &&
        yourTeamId &&
        yourTeamId !== activeTeamId;

    const finaleRound = useMemo(() => getFinaleRound(finale), [finale]);
    const finaleVisibility = finaleRound?.visibility || {};
    const finaleClues = finaleRound?.clueByTeamId || {};
    const finalePhrase = finaleRound?.phrase || '';
    const finalePhraseCharacters = useMemo(() => {
        const chars = Array.from(finalePhrase || '');
        let letterIndex = 0;
        return chars.map((char) => {
            const normalized = char === 'ß' ? 'ẞ' : char.toUpperCase();
            const isLetter = /[A-ZÄÖÜẞ]/.test(normalized);
            const entry = {
                char,
                isLetter,
                letterIndex: isLetter ? letterIndex : null,
            };
            if (isLetter) {
                letterIndex += 1;
            }
            return entry;
        });
    }, [finalePhrase]);
    const decoderIdForTeam = finaleRound?.decoderByTeamId?.[yourTeamId] || null;
    const isFinaleDecoder =
        finale?.active &&
        yourTeamId &&
        decoderIdForTeam &&
        (String(decoderIdForTeam) === String(playerId) || String(decoderIdForTeam) === String(socket.id));
    const pickingEnabled = finale?.phase === 'picking';
    const decoderLocked = finaleRound?.lockedByTeamId?.[yourTeamId];
    const decoderPicks = finaleRound?.picksByTeamId?.[yourTeamId] || [];
    const finaleClueMode = finaleVisibility?.showClues?.mode || 'none';
    const finaleVisibleTeamIds = useMemo(() => {
        if (finaleClueMode === 'all') {
            return regularTeams.map((team) => team.id);
        }
        if (finaleClueMode === 'perTeam') {
            return finaleVisibility?.showClues?.teamIds || [];
        }
        return [];
    }, [finaleClueMode, finaleVisibility?.showClues?.teamIds, regularTeams]);
    const playersById = useMemo(() => {
        return allPlayers.reduce((acc, player) => {
            acc[String(player.id)] = player;
            return acc;
        }, {});
    }, [allPlayers]);
    const yourTeam = regularTeams.find((team) => String(team.id) === String(yourTeamId));
    const pickBudgetValue = Number.isFinite(yourTeam?.pickBudget) ? yourTeam.pickBudget : 0;
    const pickLimit = Math.max(0, pickBudgetValue);
    const picksUsed = decoderPicks.length;
    const picksRemaining = Math.max(pickLimit - picksUsed, 0);

    const handleFinaleTogglePick = (index) => {
        if (!finale || !finaleRound || !yourTeamId || decoderLocked || !pickingEnabled) return;
        const alreadyPicked = decoderPicks.includes(index);
        if (!alreadyPicked && picksUsed >= pickLimit) return;
        socket.emit('finale:togglePick', { roundIndex: finale.roundIndex, teamId: yourTeamId, index });
    };

    const handleFinaleLock = () => {
        if (!finale || !finaleRound || !yourTeamId) return;
        socket.emit('finale:lockPicks', { roundIndex: finale.roundIndex, teamId: yourTeamId, locked: true });
    };

    const renderFinalePhraseText = ({ interactive = false } = {}) => {
        return (
            <span className={`finale-phrase-text ${interactive ? 'interactive' : ''}`.trim()}>
                {finalePhraseCharacters.map((entry, index) => {
                    if (!entry.isLetter) {
                        return (
                            <span className="finale-phrase-char" key={`char-${index}`}>
                                {entry.char}
                            </span>
                        );
                    }
                    const picked = decoderPicks.includes(entry.letterIndex);
                    if (!interactive) {
                        return (
                            <span className="finale-phrase-letter" key={`letter-${entry.letterIndex}-${index}`}>
                                {entry.char}
                            </span>
                        );
                    }
                    const canToggle =
                        pickingEnabled &&
                        !decoderLocked &&
                        (picked || picksUsed < pickLimit);
                    return (
                        <button
                            type="button"
                            key={`letter-${entry.letterIndex}-${index}`}
                            className={`finale-phrase-letter interactive ${picked ? 'picked' : ''}`.trim()}
                            onClick={() => handleFinaleTogglePick(entry.letterIndex)}
                            disabled={!canToggle}
                            aria-pressed={picked}
                        >
                            {entry.char}
                        </button>
                    );
                })}
            </span>
        );
    };

    // --------------------------------------
    // Render
    // --------------------------------------
    const overlayActive = !!overlayEntry;
    const inlinePlacement = inlineEntry?.ctrl?.position || 'above';
    const inlineMediaContent = inlineEntry && !overlayActive ? (
        <MediaAttachment entry={inlineEntry} variant={inlinePlacement === 'right' ? 'side' : 'inline'} onTimeSync={attachmentActions.timeSync} />
    ) : null;
    const overlayMediaContent = overlayEntry ? (
        <MediaAttachment entry={overlayEntry} variant="overlay" onTimeSync={attachmentActions.timeSync} />
    ) : null;
    const questionBlockVisible = !showMatrix && !!currentQuestion && showQuestionContainer && !overlayActive;
    const hasQuestionContent =
        !!currentQuestion && (showQuestionContainer || countdown !== null || predictionCountdown !== null || predictionActive);
    const showQuestionCard = !showMatrix && hasQuestionContent && !overlayActive;

    useEffect(() => {
        if (!hasModeratorStream) return;
        if (showQuestionContainer) {
            setIsModeratorPanelOpen(false);
        } else {
            setIsModeratorPanelOpen(true);
        }
    }, [hasModeratorStream, showQuestionContainer]);

    const increaseModeratorSize = () => setModeratorScale((prev) => Math.min(prev + 0.1, 1.6));
    const decreaseModeratorSize = () => setModeratorScale((prev) => Math.max(prev - 0.1, 0.6));
    const handleTeamSharedTextChange = useCallback((event) => {
        setTeamSharedText(event.target.value);
    }, []);

    const handleQpSubmitAnswer = useCallback((value) => {
        if (!yourTeamId) return;
        socket.emit('quizpoker:submitAnswer', { teamId: yourTeamId, value });
    }, [yourTeamId]);

    const handleQpBet = useCallback((action, amount) => {
        if (!yourTeamId) return;
        socket.emit('quizpoker:bet', { teamId: yourTeamId, action, amount });
    }, [yourTeamId]);

    const handleFfSetWager = useCallback((value, lock = false) => {
        if (!yourTeamId) return;
        socket.emit('finalfrage:setWager', { teamId: yourTeamId, value, lock });
    }, [yourTeamId]);

    const handleFfLockWager = useCallback(() => {
        if (!yourTeamId) return;
        socket.emit('finalfrage:lockWager', { teamId: yourTeamId, locked: true });
    }, [yourTeamId]);

    const handleFfSelectAnswer = useCallback((answerIndex) => {
        if (!yourTeamId) return;
        socket.emit('finalfrage:selectAnswer', { teamId: yourTeamId, answerIndex });
    }, [yourTeamId]);

    // Solange das Quiz noch nicht laeuft, sehen nicht eingeloggte Betrachter
    // (z.B. Gemeinschaftsbildschirm) die Erklaerseite. Sobald es startet,
    // springt die Ansicht automatisch zur normalen (rein lesenden) Show um.
    if (!playerId && !quizActive) {
        return <ViewerJoinInfo />;
    }

    return (
        <div
            className={`show-page ${questionBlockVisible ? 'question-visible' : ''}`}
            onClick={handleFinaleAudioClick}
        >
            <audio
                ref={finaleAudioRef}
                src={resolveMediaUrl(
                    (musicConfig[finaleConfigKey] || DEFAULT_MUSIC_CONFIG[finaleConfigKey])?.url
                    || DEFAULT_MUSIC_CONFIG.finale_decoder.url
                )}
                preload="auto"
                className="media-hidden-element"
            />
            {finaleMusicActive && finaleAudioBlocked && (
                <button
                    type="button"
                    className="finale-audio-hint"
                    onClick={(event) => {
                        event.stopPropagation();
                        handleFinaleAudioClick();
                    }}
                >
                    Klicken, um Hintergrundmusik zu aktivieren
                </button>
            )}
            {overlayMediaContent && (
                <div className="media-overlay">
                    <div className="media-overlay-content">{overlayMediaContent}</div>
                </div>
            )}
            {predictionDrawerVisible && (
                <div className={`prediction-drawer ${isPredictionDrawerOpen ? 'open' : 'closed'}`}>
                    <button
                        className="prediction-drawer-toggle"
                        type="button"
                        onClick={() => setIsPredictionDrawerOpen(prev => !prev)}
                        aria-label={isPredictionDrawerOpen ? 'Vorhersage einklappen' : 'Vorhersage ausklappen'}
                    >
                        {isPredictionDrawerOpen ? '▲' : '▼'}
                    </button>
                    <div className="prediction-drawer-card">
                        {predictionCountdown !== null && initialPredictionCountdown !== null && (
                            <ProgressBar
                                durationMs={predictionDurationMs}
                                endsAtMs={predictionEndsAtMs}
                                variant="prediction"
                            />
                        )}
                        <div className="prediction-actions">
                            <button
                                onClick={() => handlePredictAnswer(true)}
                                className={`predict-option success ${myTeamPrediction === true ? 'active' : ''}`}
                                type="button"
                            >
                                Schaffen es
                            </button>
                            <button
                                onClick={() => handlePredictAnswer(false)}
                                className={`predict-option fail ${myTeamPrediction === false ? 'active' : ''}`}
                                type="button"
                            >
                                Schaffen es nicht
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <div
                className={`moderator-panel ${isModeratorPanelOpen ? 'open' : 'closed'}`}
                style={{ '--moderator-scale': moderatorScale }}
            >
                <div className="moderator-controls">
                    <button
                        className={`moderator-panel-toggle ${!isModeratorPanelOpen && hasUnreadTeamSharedText ? 'has-unread' : ''}`}
                        onClick={() => setIsModeratorPanelOpen((prev) => !prev)}
                        aria-label={isModeratorPanelOpen ? 'Container ausblenden' : 'Container einblenden'}
                    >
                        {isModeratorPanelOpen ? '⟩' : '⟨'}
                    </button>
                    {isModeratorPanelOpen && hasModeratorStream && (
                        <div className="moderator-size-controls" aria-label="Moderatorgröße anpassen">
                            <button type="button" onClick={decreaseModeratorSize} aria-label="Livebild verkleinern">−</button>
                            <button type="button" onClick={increaseModeratorSize} aria-label="Livebild vergrößern">+</button>
                        </div>
                    )}
                </div>
                <div className={`moderator-panel-body ${isModeratorPanelOpen ? 'open' : 'closed'}`}>
                        {hasModeratorStream && (
                            <div className="moderator-iframe-list">
                                {moderatorPlayersWithLinks.map(player => (
                                    <div className="iframe-wrapper" key={player.id}>
                                        <iframe
                                            src={getSafeLink(player.link)}
                                            title={`${player.name}-liveview`}
                                            scrolling="no"
                                            allowFullScreen
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="team-shared-notes">
                            <label htmlFor="team-shared-text" className="team-shared-notes-title">
                                Team-Textblock
                            </label>
                            <textarea
                                id="team-shared-text"
                                className="team-shared-notes-input"
                                value={teamSharedText}
                                onChange={handleTeamSharedTextChange}
                                placeholder="Hier kann euer gesamtes Team live mitschreiben …"
                                disabled={!currentTeam?.id || currentTeam.name?.toLowerCase() === 'moderator'}
                            />
                        </div>
                </div>
            </div>
            <div className={`page show-page-inner ${questionBlockVisible ? 'question-visible' : ''}`}>
                {(!matrixVisible && currentQuestion) && (
                    <div className="floating-pill">
                        {currentQuestion.kategorie} · {currentQuestion.points} Punkte
                    </div>
                )}

                <div className={`section-stack ${questionBlockVisible ? 'question-visible' : ''}`}>
                    {finale?.active && finaleRound && (() => {
                        const showBasePhrase = finaleVisibility.showPhrase && (!isFinaleDecoder || !pickingEnabled);
                        const showDecoderPhrase = isFinaleDecoder && pickingEnabled;
                        return (
                            <div className="finale-layout">
                                <div className="card finale-card finale-header-card">
                                    <div className="finale-card-header">
                                        <span className="finale-title">Finale – Transmitter</span>
                                    </div>
                                    <div className="finale-card-body">
                                        {finaleVisibility.showCategory && (
                                            <div className="finale-row">
                                                <span className="finale-label">Kategorie</span>
                                                <span className="finale-value">{finaleRound.category || '—'}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="finale-main">
                                    {showBasePhrase && (
                                        <div className="card finale-phrase-card">
                                            <div className="finale-card-body">
                                                <div className="finale-row">
                                                    <span className="finale-label">Phrase</span>
                                                    <span className="finale-value finale-phrase">
                                                        {finaleRound.phrase
                                                            ? renderFinalePhraseText()
                                                            : '—'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Decoder phrase is only visible while picking is enabled. */}
                                    {showDecoderPhrase && (
                                        <div className="card finale-decoder-panel finale-phrase-card">
                                            <div className="finale-card-body">
                                                <div className="finale-row">
                                                    <span className="finale-label">Phrase</span>
                                                    <span className="finale-value finale-phrase finale-phrase-decoder">
                                                        {finaleRound.phrase
                                                            ? renderFinalePhraseText({ interactive: true })
                                                            : '—'}
                                                    </span>
                                                </div>
                                                <div className="finale-pick-status">
                                                    <span>
                                                        Picks übrig: <strong>{picksRemaining}</strong>
                                                    </span>
                                                </div>
                                                <Button
                                                    variant="primary"
                                                    onClick={handleFinaleLock}
                                                    disabled={decoderLocked}
                                                >
                                                    {decoderLocked ? 'Eingeloggt' : 'Picks einloggen'}
                                                </Button>
                                            </div>
                                        </div>
                                    )}

                                    {(finaleVisibility.showDecoders || (finaleClueMode !== 'none' && finaleVisibleTeamIds.length > 0)) && (
                                        <div className="finale-info-grid">
                                            {finaleVisibility.showDecoders && (
                                                <div className="card finale-info-card">
                                                    <div className="finale-card-body">
                                                        <div className="finale-decoder-list">
                                                            {regularTeams.map((team) => {
                                                                const decoderId = finaleRound?.decoderByTeamId?.[team.id];
                                                                const decoderName = decoderId ? playersById[String(decoderId)]?.name || `ID ${decoderId}` : '—';
                                                                return (
                                                                    <div className="finale-decoder-entry" key={team.id}>
                                                                        <span className="finale-label">{team.name}</span>
                                                                        <span className="finale-value">{decoderName}</span>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            {finaleClueMode !== 'none' && finaleVisibleTeamIds.length > 0 && (
                                                <div className="card finale-info-card">
                                                    <div className="finale-card-body">
                                                        <div className="finale-clue-grid">
                                                            {finaleVisibleTeamIds.map((teamId) => {
                                                                const team = regularTeams.find((entry) => entry.id === teamId);
                                                                if (!team) return null;
                                                                const clueText = finaleClues?.[teamId] || '';
                                                                return (
                                                                    <div className="finale-clue-card" key={teamId}>
                                                                        <span className="finale-label">{team.name}</span>
                                                                        <span className="finale-clue-text">
                                                                            {finale?.phase === 'reveal' ? (
                                                                                <DecodeText text={clueText || '—'} trigger={finaleRevealToken} />
                                                                            ) : (
                                                                                clueText || '—'
                                                                            )}
                                                                        </span>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })()}

                    {quizpokerFinale?.active && (
                        <FinaleQuizpokerStage
                            finale={quizpokerFinale}
                            teams={regularTeams}
                            allPlayers={allPlayers}
                            yourTeamId={yourTeamId}
                            onSubmitAnswer={handleQpSubmitAnswer}
                            onBet={handleQpBet}
                        />
                    )}

                    {finalfrageFinale?.active && (
                        <FinaleFinalfrageStage
                            finale={finalfrageFinale}
                            teams={regularTeams}
                            allPlayers={allPlayers}
                            yourTeamId={yourTeamId}
                            onSetWager={handleFfSetWager}
                            onLockWager={handleFfLockWager}
                            onSelectAnswer={handleFfSelectAnswer}
                        />
                    )}

                    {renderMatrix()}

                    {showQuestionCard && (
                        <div className="card question-card">
                            {predictionCountdown !== null && initialPredictionCountdown !== null && String(activeTeamId) === String(yourTeamId) && (
                                <ProgressBar
                                    durationMs={predictionDurationMs}
                                    endsAtMs={predictionEndsAtMs}
                                    variant="prediction"
                                />
                            )}
                            {countdown !== null && initialCountdown !== null && (
                                <ProgressBar
                                    durationMs={countdownDurationMs}
                                    endsAtMs={countdownEndsAtMs}
                                    paused={paused}
                                />
                            )}

                            {inlineMediaContent && inlinePlacement === 'above' && (
                                <div className="media-block media-block-above">{inlineMediaContent}</div>
                            )}

                            {showQuestionContainer && currentQuestion && (
                                inlinePlacement === 'right' && inlineMediaContent ? (
                                    <div className="question-media-grid">
                                        <div className="question-body">
                                            <p className="question-text">{currentQuestion.frage}</p>
                                            <div className="answers-section">
                                                {renderAnswers()}
                                            </div>
                                        </div>
                                        <div className="media-block media-block-side">
                                            {inlineMediaContent}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="question-body">
                                        <p className="question-text">{currentQuestion.frage}</p>
                                        <div className="answers-section">
                                            {renderAnswers()}
                                        </div>
                                    </div>
                                )
                            )}
                        </div>
                    )}

                </div>
            </div>

            {!quizActive && !yourTeamId && safeStorage.getItem('isModerator') !== '1' && (
                <TeamJoinPanel playerId={playerId} />
            )}

            <TeamSheet
                teams={regularTeams}
                predictionAnswers={predictionAnswers}
                predictionsRevealed={predictionsRevealed}
                activeTeamId={activeTeamId}
                allPlayers={allPlayers}
                showPickBudget={finale?.active}
                besserwisserState={besserwisserState}
                quizpokerState={quizpokerFinale?.active ? quizpokerFinale : null}
                finalfrageState={finalfrageFinale?.active ? finalfrageFinale : null}
                yourTeamId={yourTeamId}
                myTeamJoinCode={(quizActive && !paused) ? null : myTeamJoinCode}
            />
        </div>
    );
}

export default ShowPage;
